import styled, { css, DefaultTheme, keyframes } from 'styled-components';
import { BtnColorType, StyleType } from './type';

const button_loading_spinner = keyframes`
	
	from {
	  transform: rotate(0turn);
	}
  
	to {
	  transform: rotate(1turn);
	}
  
`;

const color_mapper = {
	white: (theme: DefaultTheme) => theme.colors.gray.gray_white,
};

export const BaseButton = styled.button<{
	_width?: string;
	margin?: string;
	is_loading?: boolean;
	color_type?: BtnColorType;
}>`
	height: 40px;
	width: ${({ _width }) => _width ?? 'max-content'};
	white-space: nowrap;
	padding: 0 24px;
	border-radius: 8px;

	${({ margin }) => margin && `margin: ${margin}`};

	${({ theme }) => theme.typo.body.p_bold};

	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	cursor: pointer;
	transition: background-color 0.2s, border 0.2s;

	text-align: center;

	&:disabled {
		cursor: unset;
	}
`;

// Primary
export const PrimaryButton = styled(BaseButton)<{ style_type: StyleType }>`
	// type - filled
	${(props) =>
		props.style_type === 'filled' &&
		css`
			--color: ${(props) => props.theme.colors.gray.gray_white};
			color: var(--color);
			background-color: ${({ theme }) => theme.colors.primary.primary50_key};

			&:hover {
				background-color: ${(props) => props.theme.colors.primary.primary60};
			}

			&:active {
				background-color: ${(props) => props.theme.colors.primary.primary70};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				background-color: ${(props) => props.theme.colors.gray.gray15};
			}
		`}

	// type - tonal
    ${(props) =>
		props.style_type === 'tonal' &&
		css`
			--color: ${(props) => props.theme.colors.primary.primary80_darken};
			color: var(--color);
			background-color: ${(props) => props.theme.colors.primary.primary10_lighten};

			&:active {
				background-color: ${(props) => props.theme.colors.primary.primary15};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				background-color: ${(props) => props.theme.colors.gray.gray15};
			}
		`}

    // type - outlined
    ${({ style_type, theme, color_type }) =>
		style_type === 'outlined' &&
		css`
			--color: ${() => {
				if (!color_type || color_type === 'default')
					return theme.colors.primary.primary80_darken;
				else return color_mapper[color_type](theme);
			}};
			color: var(--color);
			background-color: ${({ theme }) => theme.colors.gray.gray_white};
			border: 1px solid ${(props) => props.theme.colors.primary.primary50_key};

			&:hover {
				background-color: ${(props) => props.theme.colors.primary.primary10_lighten};
			}

			&:active {
				background-color: ${(props) => props.theme.colors.primary.primary15};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				border-color: ${(props) => props.theme.colors.gray.gray15};
				background-color: ${({ theme }) => theme.colors.gray.gray_white};
			}
		`}

        // type - text
        ${(props) =>
		props.style_type === 'text' &&
		css`
			--color: ${(props) => props.theme.colors.primary.primary50_key};
			color: var(--color);
			background-color: transparent;

			&:hover {
				background-color: ${(props) => props.theme.colors.primary.primary10_lighten};
			}

			&:active {
				background-color: ${(props) => props.theme.colors.primary.primary15};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				background-color: transparent;
			}
		`}

		${({ is_loading }) =>
		is_loading &&
		css`
			& > span {
				visibility: hidden;
			}

			&::after {
				content: '';
				position: absolute;
				width: 16px;
				height: 16px;
				top: calc(50% - (8px + 2px));
				left: calc(50% - (8px + 2px));
				margin: auto;
				border: 2px solid transparent;
				border-top-color: var(--color);
				border-radius: 50%;
				animation: ${button_loading_spinner} 1s ease infinite;
			}
		`}
`;

export const LargePrimaryButton = styled(PrimaryButton)`
	display: block;
	width: 100%;
	height: 56px;
	padding: 0;
`;

export const SmallPrimaryButton = styled(PrimaryButton)`
	height: 26.5px;
	padding: 0 8px;
	border-radius: 4px;
	&::after {
		width: 8px;
		height: 8px;
		top: calc(50% - (4px + 2px));
		left: calc(50% - (4px + 2px));
	}
`;

// Gray
export const GrayButton = styled(BaseButton)<{ style_type: StyleType }>`
	// type - filled
	${({ style_type, color_type, theme }) =>
		style_type === 'filled' &&
		css`
			--color: ${() => {
				if (!color_type || color_type === 'default') return theme.colors.gray.gray_white;
				else return color_mapper[color_type](theme);
			}};
			color: var(--color);
			background-color: ${theme.colors.gray.gray80_text};

			&:hover {
				background-color: ${theme.colors.gray.gray90};
			}

			&:active {
				background-color: ${theme.colors.gray.gray_black};
			}

			&:disabled {
				color: ${theme.colors.gray.gray30};
				background-color: ${theme.colors.gray.gray15};
			}
		`}

	// type - tonal
	${({ style_type }) =>
		style_type === 'tonal' &&
		css`
			--color: ${(props) => props.theme.colors.gray.gray80_text};
			color: var(--color);
			background-color: ${(props) => props.theme.colors.gray.gray10};

			&:active {
				background-color: ${(props) => props.theme.colors.gray.gray15};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				background-color: ${(props) => props.theme.colors.gray.gray15};
			}
		`}

	// type - outlined
    ${({ style_type, color_type, theme }) =>
		style_type === 'outlined' &&
		css`
			--color: ${() => {
				if (!color_type || color_type === 'default') return theme.colors.gray.gray80_text;
				else return color_mapper[color_type](theme);
			}};
			color: var(--color);
			background-color: ${() => {
				if (!color_type || color_type === 'default') return theme.colors.gray.gray_white;
				else return 'transparent';
			}};
			border: 1px solid ${(props) => props.theme.colors.gray.gray15};

			&:hover {
				background-color: ${() =>
					(color_type === 'default' || !color_type) && theme.colors.gray.gray10};
			}

			&:active {
				background-color: ${() =>
					(color_type === 'default' || !color_type) && theme.colors.gray.gray15};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				border-color: ${(props) => props.theme.colors.gray.gray15};
				background-color: ${({ theme }) => theme.colors.gray.gray_white};
				& > span > span > svg {
					fill: ${(props) => props.theme.colors.gray.gray30};
				}
			}
		`}

        // type - text
        ${({ style_type }) =>
		style_type === 'text' &&
		css`
			--color: ${(props) => props.theme.colors.gray.gray80_text};
			color: var(--color);
			background-color: transparent;

			&:hover {
				background-color: ${(props) => props.theme.colors.gray.gray10};
			}

			&:active {
				background-color: ${(props) => props.theme.colors.gray.gray15};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				background-color: transparent;
			}
		`}

		${({ is_loading }) =>
		is_loading &&
		css`
			& > span {
				visibility: hidden;
			}

			&::after {
				content: '';
				position: absolute;
				width: 16px;
				height: 16px;
				top: calc(50% - (8px + 2px));
				left: calc(50% - (8px + 2px));
				margin: auto;
				border: 2px solid transparent;
				border-top-color: var(--color);
				border-radius: 50%;
				animation: ${button_loading_spinner} 1s ease infinite;
			}
		`}
`;

// Black
export const BlackButton = styled(BaseButton)<{ style_type: StyleType }>`
	// type - filled
	${({ style_type }) =>
		style_type === 'filled' &&
		css`
			--color: ${(props) => props.theme.colors.gray.gray_white};
			color: var(--color);
			background-color: ${(props) => props.theme.colors.gray.gray_black};

			&:hover {
				background-color: ${(props) => props.theme.colors.gray.gray_black};
			}

			&:active {
				background-color: ${(props) => props.theme.colors.gray.gray_black};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				background-color: ${(props) => props.theme.colors.gray.gray15};
			}
		`}

	// type - tonal
	${({ style_type }) =>
		style_type === 'tonal' &&
		css`
			--color: ${(props) => props.theme.colors.gray.gray_black};
			color: var(--color);
			background-color: ${(props) => props.theme.colors.gray.gray10};

			&:active {
				background-color: ${(props) => props.theme.colors.gray.gray15};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				background-color: ${(props) => props.theme.colors.gray.gray15};
			}
		`}

	// type - outlined
    ${({ style_type }) =>
		style_type === 'outlined' &&
		css`
			--color: ${(props) => props.theme.colors.gray.gray_black};
			color: var(--color);
			background-color: ${({ theme }) => theme.colors.gray.gray_white};
			border: 1px solid ${(props) => props.theme.colors.gray.gray15};

			&:hover {
				background-color: ${(props) => props.theme.colors.gray.gray10};
			}

			&:active {
				background-color: ${(props) => props.theme.colors.gray.gray15};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				border-color: ${(props) => props.theme.colors.gray.gray15};
				background-color: ${({ theme }) => theme.colors.gray.gray_white};
				& > span > span > svg {
					fill: ${(props) => props.theme.colors.gray.gray30};
				}
			}
		`}

        // type - text
        ${({ style_type }) =>
		style_type === 'text' &&
		css`
			--color: ${(props) => props.theme.colors.gray.gray_black};
			color: var(--color);
			background-color: transparent;

			&:hover {
				background-color: ${(props) => props.theme.colors.gray.gray10};
			}

			&:active {
				background-color: ${(props) => props.theme.colors.gray.gray15};
			}

			&:disabled {
				color: ${(props) => props.theme.colors.gray.gray30};
				background-color: transparent;
			}
		`}

		${({ is_loading }) =>
		is_loading &&
		css`
			& > span {
				visibility: hidden;
			}

			&::after {
				content: '';
				position: absolute;
				width: 16px;
				height: 16px;
				top: calc(50% - (8px + 2px));
				left: calc(50% - (8px + 2px));
				margin: auto;
				border: 2px solid transparent;
				border-top-color: var(--color);
				border-radius: 50%;
				animation: ${button_loading_spinner} 1s ease infinite;
			}
		`}
`;

export const LargeGrayButton = styled(GrayButton)`
	display: block;
	width: 100%;
	height: 56px;
	padding: 0;
`;

export const SmallGrayButton = styled(GrayButton)`
	height: 26.5px;
	padding: 0 8px;
	border-radius: 4px;

	&::after {
		width: 8px;
		height: 8px;
		top: calc(50% - (4px + 2px));
		left: calc(50% - (4px + 2px));
	}
`;

// Icon - Primary
export const IconPrimaryButton = styled(PrimaryButton)`
	padding: 0 24px 0 16px;

	& > span {
		${({ theme }) => theme.common.flexStart};
	}

	& > span > span {
		margin-right: 8px;
	}
`;

// Icon - Gray
export const IconGrayButton = styled(GrayButton)`
	padding: 0 24px 0 16px;
	cursor: pointer;

	& > span {
		${({ theme }) => theme.common.flexStart};
	}

	& > span > span {
		margin-right: 8px;
	}
`;

// Icon - Black
export const IconBlackButton = styled(BlackButton)`
	padding: 0 24px 0 16px;
	cursor: pointer;

	& > span {
		${({ theme }) => theme.common.flexStart};
	}

	& > span > span {
		margin-right: 8px;
	}
`;

// Icon - none
export const IconButton = styled.button<{ _disabled: boolean; is_loading?: boolean }>`
	width: 40px;
	height: 40px;
	border-radius: 8px;
	border: 1px solid ${({ theme }) => theme.colors.gray.gray15};
	background-color: ${({ theme }) => theme.colors.gray.gray_white};
	position: relative;
	cursor: pointer;
	transition: 0.2s background-color;

	&:hover {
		background-color: ${(props) => props.theme.colors.gray.gray10};
	}

	&:active {
		background-color: ${(props) => props.theme.colors.gray.gray15};
	}

	${({ _disabled, theme }) =>
		_disabled &&
		`
		background-color: ${theme.colors.gray.gray15};
	`}

	${({ is_loading }) =>
		is_loading &&
		css`
			& > span {
				visibility: hidden;
			}

			&::after {
				content: '';
				position: absolute;
				width: 16px;
				height: 16px;
				top: calc(50% - (8px + 2px));
				left: calc(50% - (8px + 2px));
				margin: auto;
				border: 2px solid transparent;
				border-top-color: ${({ theme }) => theme.colors.gray.gray80_text};
				border-radius: 50%;
				animation: ${button_loading_spinner} 1s ease infinite;
			}
		`}
`;

// Icon_toggle - small
export const SmallIconTogglebutton = styled(BaseButton)<{
	toggle_on: boolean;
	_margin?: string;
}>`
	background-color: transparent;
	width: 40px;
	padding: 0;

	&:hover {
		background-color: ${(props) => props.theme.colors.gray.gray05_bg};
	}

	${(props) =>
		props.toggle_on &&
		css`
			background-color: ${(props) => props.theme.colors.gray.gray10};
			&:hover {
				background-color: ${(props) => props.theme.colors.gray.gray10};
			}
		`};
	${({ _margin }) => _margin && `margin: ${_margin}`};
`;

// Icon_toggle - normal
export const IconTogglebutton = styled(SmallIconTogglebutton)`
	color: ${(props) => props.theme.colors.gray.gray80_text};
	width: auto;
	padding: 0 24px 0 16px;

	& > span {
		margin-right: 8px;
	}
`;

// Danger
export const DangerButton = styled(BaseButton)`
	--color: ${(props) => props.theme.colors.system.red_darken}
	color: var(--color);
	background-color: ${(props) => props.theme.colors.system.red_lighten};

	&:hover {
		color: ${(props) => props.theme.colors.gray.gray_white};
		background-color: ${(props) => props.theme.colors.system.red};
	}

	&:active {
		color: ${(props) => props.theme.colors.gray.gray_white};
		background-color: ${(props) => props.theme.colors.system.red_darken};
	}

	&:disabled {
		color: ${(props) => props.theme.colors.gray.gray30};
		background-color: ${(props) => props.theme.colors.gray.gray15};
	}

	${({ is_loading, theme }) =>
		is_loading &&
		css`
			& > span {
				visibility: hidden;
			}

			&::after {
				content: '';
				position: absolute;
				width: 16px;
				height: 16px;
				top: calc(50% - (8px + 2px));
				left: calc(50% - (8px + 2px));
				margin: auto;
				border: 2px solid transparent;
				border-top-color: var(--color);
				border-radius: 50%;
				animation: ${button_loading_spinner} 1s ease infinite;
			}
		`}
`;
